import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Button,
  Icon,
  List,
} from "semantic-ui-react";

// UX
import CardLeavePolicyUX from "./CardLeavePolicyUX";

// Common
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Component
import CardLeaveSettingsPanel from "./CardLeaveSettingsPanel";

// Interface
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  LeaveTypeListApiType,
} from "./sequence/LeavePolicy";

// Type
type CardLeavePolicyProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  LeavePolicySeqeunce: State["LeavePolicySequence"];
  // options
  languageUX: "en" | "th";
} & PickedProps;

type LeaveTypeListSegmentProps = {
  onAddType?: () => void;
  onSelectType?: (leave: any) => void;
  data: LeaveTypeListApiType[];
  selectedLeave: number | null;
};

const CardLeavePolicy = (props: CardLeavePolicyProps) => {
  const intl = useIntl();

  // useState
  const [openSettingsPanel, setOpenSettingsPanel] = useState<boolean>(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveTypeListApiType | null>(null);

  useEffect(() => {
    props.runSequence({ sequence: "LeavePolicy", restart: true });
  }, []);

  // Clear After Save Success
  useEffect(() => {
    if (props.successMessage?.[CARD_NAME]) {
      setOpenSettingsPanel(false);
      setSelectedLeaveType(null);
    }
  }, [props.successMessage]);

  const leaveTypeList = useMemo(() => {
    return props.LeavePolicySeqeunce?.leaveTypeList || [];
  }, [props.LeavePolicySeqeunce?.leaveTypeList]);

  return (
    <>
      <SnackMessage
        // func
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_NAME}`, null);
          props.setProp(`successMessage.${CARD_NAME}`, null);
        }}
        error={props.errorMessage?.[CARD_NAME]}
        success={props.successMessage?.[CARD_NAME]}
        languageUX={props.languageUX}
      />

      <CardLeavePolicyUX
        leaveTypeList={
          <LeaveTypeListSegment
            onAddType={() => {
              setSelectedLeaveType(null);
              setOpenSettingsPanel(true);
            }}
            onSelectType={(leave) => {
              setSelectedLeaveType(leave);
              setOpenSettingsPanel(true);
            }}
            data={leaveTypeList}
            selectedLeave={selectedLeaveType?.id ?? null}
          />
        }
        leaveSettingsPanel={
          openSettingsPanel ? (
            <CardLeaveSettingsPanel
              runSequence={props.runSequence}
              setProp={props.setProp}
              buttonLoadCheck={props.buttonLoadCheck}
              successMessage={props.successMessage}
              onClose={() => {
                setOpenSettingsPanel(false);
                setSelectedLeaveType(null);
              }}
              data={selectedLeaveType}
            />
          ) : null
        }
      />
    </>
  );
};

/**========================================================================
 **                              COMPONENT
 *========================================================================**/

const LeaveTypeListSegment = (props: LeaveTypeListSegmentProps) => {

  const handleAddType = () => {
    props.onAddType?.();
  };

  const handleSelectLeave = (leave: LeaveTypeListApiType) => {
    props.onSelectType?.(leave);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: "2rem" }}>
        <label style={{ fontSize: "18px", fontWeight: "bold" }}>Leave Type List</label>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
        <Button 
          id="CardLeaveRequest-Button-AddType"
          basic
          color="blue"
          size="small"
          onClick={handleAddType}>
          {
            <div>
              <Icon name="plus" />
              <label>Add Type</label>
            </div>
          }
        </Button>
      </div>
      <div style={{ marginBottom: "1rem" }}>
        <List selection>
          {props.data.map((leave) => (
            <List.Item
              id={`CardLeavePolicy-ListItem-${leave.id}`}
              key={leave.id}
              active={props.selectedLeave === leave.id}
              onClick={() => handleSelectLeave(leave)}
              style={{
                padding: "1em",
                borderRadius: "5px",
                backgroundColor: props.selectedLeave === leave.id ? "#e6f7ff" : "transparent",
                color: props.selectedLeave === leave.id ? "#1677ff" : "black",
                cursor: "pointer",
              }}
            >
              {leave.name}
            </List.Item>
          ))}
        </List>
      </div>
    </div>
  );
};

CardLeavePolicy.displayName = "CardLeavePolicy";

export default React.memo(CardLeavePolicy);
